import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useChannel } from '@/context/ChannelContext'
import {
  METRIC_FORMAT,
  METRIC_KEYS,
  METRIC_LABELS,
  getByCampaignReport,
  getCampaignsList,
  getDailyReport,
  type CampaignRow,
  type CampaignsListEntry,
  type DailyRow,
  type MetricKey,
  type MetricRow,
} from '@/api/adsReport'

const MAX_DAYS = 90
const VISIBLE_COLUMNS_KEY = 'adsReport.visibleColumns'

// Дефолт по дизайну: 6 колонок в daily-таблице
const DEFAULT_VISIBLE: MetricKey[] = ['cost', 'pdp', 'cpaPdp', 'churnRate', 'netPdp', 'netPdpCost']

type Preset = 'today' | 'yesterday' | '7d' | '30d' | 'thisMonth' | 'lastMonth' | 'custom'

export function AnalyticsPage() {
  const { selectedChannel } = useChannel()
  const channelUUID = selectedChannel?.uuid || ''

  const initial = presetRange('7d')
  const [from, setFrom] = useState(initial.from)
  const [to, setTo] = useState(initial.to)
  const [campaignIds, setCampaignIds] = useState<string[]>([])
  const [showDaily, setShowDaily] = useState(true)
  const [showByCampaign, setShowByCampaign] = useState(true)
  const [visibleColumns, setVisibleColumns] = useState<MetricKey[]>(() => loadVisibleColumns())
  const [activeTab, setActiveTab] = useState<'ads' | 'links'>('ads')
  const [chartMetric, setChartMetric] = useState<MetricKey>('cost')

  useEffect(() => {
    try { localStorage.setItem(VISIBLE_COLUMNS_KEY, JSON.stringify(visibleColumns)) } catch {}
  }, [visibleColumns])

  const params = useMemo(
    () => ({ channelUUID, from, to, campaignIds }),
    [channelUUID, from, to, campaignIds],
  )

  const campaignsQuery = useQuery({
    queryKey: ['adsReport', 'campaigns', channelUUID, from, to],
    queryFn: () => getCampaignsList({ channelUUID, from, to }),
    enabled: !!channelUUID,
    staleTime: 60_000,
  })

  const dailyQuery = useQuery({
    queryKey: ['adsReport', 'daily', params],
    queryFn: () => getDailyReport(params),
    enabled: !!channelUUID && showDaily,
    staleTime: 60_000,
  })

  const byCampaignQuery = useQuery({
    queryKey: ['adsReport', 'byCampaign', params],
    queryFn: () => getByCampaignReport(params, { limit: 50 }),
    enabled: !!channelUUID && showByCampaign,
    staleTime: 60_000,
  })

  const campaigns = campaignsQuery.data?.campaigns || []
  const dailyRows = dailyQuery.data?.rows || []
  const dailyTotals = dailyQuery.data?.totals
  const campaignRows = byCampaignQuery.data?.rows || []
  const campaignTotals = byCampaignQuery.data?.totals

  return (
    <div className="ar-content">
      <div className="hero">
        <div className="hero-row">
          <div>
            <p className="hero-eyebrow">Аналитика рекламы</p>
            <h1 className="hero-title">
              Отчёт <em>по&nbsp;кампаниям</em>
            </h1>
          </div>
          <div className="ar-tabs">
            <button className={activeTab === 'ads' ? 'on' : ''} onClick={() => setActiveTab('ads')}>Реклама</button>
            <button className={activeTab === 'links' ? 'on' : ''} onClick={() => setActiveTab('links')}>Ссылки</button>
          </div>
        </div>
      </div>

      {activeTab === 'links' ? (
        <div className="ar-card">
          <div className="ar-empty">Аналитика по ссылкам — скоро.</div>
        </div>
      ) : (
        <>
          <div className="ar-filters">
            <DateRangeControl
              from={from}
              to={to}
              onChange={(f, t) => { setFrom(f); setTo(t) }}
            />
            <CampaignsControl
              campaigns={campaigns}
              selected={campaignIds}
              onChange={setCampaignIds}
              isLoading={campaignsQuery.isLoading}
            />
            <SectionsToggle
              showDaily={showDaily}
              showByCampaign={showByCampaign}
              onToggleDaily={() => setShowDaily((v) => !v)}
              onToggleByCampaign={() => setShowByCampaign((v) => !v)}
            />
            <ColumnsControl visible={visibleColumns} onChange={setVisibleColumns} />
          </div>

          <ChartCard
            rows={dailyRows}
            selectedMetric={chartMetric}
            onSelectMetric={setChartMetric}
            isLoading={dailyQuery.isLoading}
          />

          {showDaily && (
            <DailyTable
              rows={dailyRows}
              totals={dailyTotals}
              visibleColumns={visibleColumns}
              isLoading={dailyQuery.isLoading}
              isError={dailyQuery.isError}
              errorMessage={(dailyQuery.error as Error)?.message}
            />
          )}

          {showByCampaign && (
            <ByCampaignTable
              rows={campaignRows}
              totals={campaignTotals}
              visibleColumns={visibleColumns}
              isLoading={byCampaignQuery.isLoading}
              isError={byCampaignQuery.isError}
              errorMessage={(byCampaignQuery.error as Error)?.message}
            />
          )}
        </>
      )}

      <Styles />
    </div>
  )
}

// ────────────────── DATE RANGE ──────────────────

function DateRangeControl({
  from,
  to,
  onChange,
}: { from: string; to: string; onChange: (from: string, to: string) => void }) {
  const [open, setOpen] = useState(false)
  const [draftFrom, setDraftFrom] = useState(from)
  const [draftTo, setDraftTo] = useState(to)
  const [error, setError] = useState<string | null>(null)
  const popRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    setDraftFrom(from)
    setDraftTo(to)
    setError(null)
    function onClick(e: MouseEvent) {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open, from, to])

  function applyPreset(p: Preset) {
    if (p === 'custom') return
    const { from: f, to: t } = presetRange(p)
    setDraftFrom(f); setDraftTo(t); setError(null)
  }

  function confirm() {
    if (draftFrom > draftTo) { setError('Дата «с» не может быть позже «по»'); return }
    if (daysBetween(draftFrom, draftTo) > MAX_DAYS) {
      setError(`Максимум ${MAX_DAYS} дней`); return
    }
    onChange(draftFrom, draftTo)
    setOpen(false)
  }

  const label = `${formatRu(from)} – ${formatRu(to)}`
  return (
    <div className="ar-filter-wrap">
      <button className={`ar-filter-btn ${open ? 'on' : ''}`} onClick={() => setOpen((v) => !v)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" />
        </svg>
        <span>{label}</span>
      </button>
      {open && (
        <div className="ar-popover" ref={popRef}>
          <div className="ar-popover-head">Период</div>
          <div className="ar-popover-presets">
            {([
              ['today', 'Сегодня'],
              ['yesterday', 'Вчера'],
              ['7d', '7 дней'],
              ['30d', '30 дней'],
              ['thisMonth', 'Этот месяц'],
              ['lastMonth', 'Прошлый месяц'],
            ] as Array<[Preset, string]>).map(([p, label]) => (
              <button key={p} className="ar-preset" onClick={() => applyPreset(p)}>
                {label}
              </button>
            ))}
          </div>
          <div className="ar-date-row">
            <label>
              <span>С</span>
              <input type="date" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} />
            </label>
            <label>
              <span>По</span>
              <input type="date" value={draftTo} onChange={(e) => setDraftTo(e.target.value)} />
            </label>
          </div>
          {error && <div className="ar-popover-error">{error}</div>}
          <div className="ar-popover-foot">
            <button className="ar-btn-link" onClick={() => setOpen(false)}>Отменить</button>
            <button className="ar-btn-primary" onClick={confirm}>Подтвердить</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ────────────────── CAMPAIGNS MULTI-SELECT ──────────────────

function CampaignsControl({
  campaigns,
  selected,
  onChange,
  isLoading,
}: {
  campaigns: CampaignsListEntry[]
  selected: string[]
  onChange: (ids: string[]) => void
  isLoading: boolean
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [draft, setDraft] = useState<string[]>(selected)
  const popRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    setDraft(selected)
    setSearch('')
    function onClick(e: MouseEvent) {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open, selected])

  const filtered = useMemo(() => {
    if (!search.trim()) return campaigns
    const q = search.trim().toLowerCase()
    return campaigns.filter((c) =>
      c.campaignName.toLowerCase().includes(q) ||
      c.ads.some((a) => a.adTitle.toLowerCase().includes(q)),
    )
  }, [campaigns, search])

  const allSelected = draft.length === campaigns.length && campaigns.length > 0
  const label =
    selected.length === 0 || selected.length === campaigns.length
      ? `Все кампании${campaigns.length ? ` · ${campaigns.length}` : ''}`
      : `${selected.length} ${pluralCampaigns(selected.length)}`

  function toggle(id: string) {
    setDraft((d) => d.includes(id) ? d.filter((x) => x !== id) : [...d, id])
  }
  function toggleAll() {
    setDraft(allSelected ? [] : campaigns.map((c) => c.campaignID))
  }
  function confirm() {
    onChange(draft.length === campaigns.length ? [] : draft)
    setOpen(false)
  }

  return (
    <div className="ar-filter-wrap">
      <button className={`ar-filter-btn ${open ? 'on' : ''}`} onClick={() => setOpen((v) => !v)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 6h16M7 12h10M10 18h4" />
        </svg>
        <span>{label}</span>
      </button>
      {open && (
        <div className="ar-popover ar-popover-wide" ref={popRef}>
          <div className="ar-popover-head">Кампании</div>
          <input
            className="ar-search"
            placeholder="Поиск по названию или объявлению…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="ar-checkbox-row" onClick={toggleAll}>
            <span className={`ar-checkbox ${allSelected ? 'on' : ''}`} />
            <b>{allSelected ? 'Снять все' : 'Выбрать все'}</b>
            <span className="ar-mute">{campaigns.length} кампаний</span>
          </div>
          <div className="ar-campaigns-list">
            {isLoading ? (
              <div className="ar-empty">Загружаем кампании…</div>
            ) : filtered.length === 0 ? (
              <div className="ar-empty">Не найдено</div>
            ) : (
              filtered.map((c) => (
                <div key={c.campaignID} className="ar-campaign-opt" onClick={() => toggle(c.campaignID)}>
                  <span className={`ar-checkbox ${draft.includes(c.campaignID) ? 'on' : ''}`} />
                  <div className="ar-campaign-meta">
                    <code>{c.campaignName}</code>
                    {c.ads.slice(0, 2).map((a) => (
                      <span key={a.adID} className="ar-campaign-ad">{a.adTitle}</span>
                    ))}
                    {c.ads.length > 2 && <span className="ar-mute">+ {c.ads.length - 2} объявлений</span>}
                  </div>
                  <span className="ar-mute ar-campaign-spend">{formatRub(c.totalSpend)}</span>
                </div>
              ))
            )}
          </div>
          <div className="ar-popover-foot">
            <button className="ar-btn-link" onClick={() => setOpen(false)}>Отменить</button>
            <button className="ar-btn-primary" onClick={confirm}>Подтвердить</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ────────────────── SECTIONS TOGGLE ──────────────────

function SectionsToggle({
  showDaily, showByCampaign, onToggleDaily, onToggleByCampaign,
}: {
  showDaily: boolean; showByCampaign: boolean
  onToggleDaily: () => void; onToggleByCampaign: () => void
}) {
  return (
    <div className="ar-segments">
      <button className={showDaily ? 'on' : ''} onClick={onToggleDaily}>По дням</button>
      <button className={showByCampaign ? 'on' : ''} onClick={onToggleByCampaign}>По кампаниям</button>
    </div>
  )
}

// ────────────────── COLUMNS ──────────────────

function ColumnsControl({
  visible, onChange,
}: { visible: MetricKey[]; onChange: (cols: MetricKey[]) => void }) {
  const [open, setOpen] = useState(false)
  const popRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  function toggle(k: MetricKey) {
    if (visible.includes(k)) onChange(visible.filter((x) => x !== k))
    else onChange([...visible, k])
  }
  function toggleAll() {
    if (visible.length === METRIC_KEYS.length) onChange(DEFAULT_VISIBLE)
    else onChange(METRIC_KEYS)
  }

  return (
    <div className="ar-filter-wrap">
      <button className={`ar-filter-btn ${open ? 'on' : ''}`} onClick={() => setOpen((v) => !v)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 5h18M3 12h18M3 19h18" />
        </svg>
        <span>Колонки · {visible.length}</span>
      </button>
      {open && (
        <div className="ar-popover" ref={popRef}>
          <div className="ar-popover-head">Видимые колонки</div>
          <div className="ar-checkbox-row" onClick={toggleAll}>
            <span className={`ar-checkbox ${visible.length === METRIC_KEYS.length ? 'on' : ''}`} />
            <b>{visible.length === METRIC_KEYS.length ? 'Скрыть лишнее' : 'Показать все'}</b>
          </div>
          <div className="ar-columns-list">
            {METRIC_KEYS.map((k) => (
              <div key={k} className="ar-campaign-opt" onClick={() => toggle(k)}>
                <span className={`ar-checkbox ${visible.includes(k) ? 'on' : ''}`} />
                <span>{METRIC_LABELS[k]}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ────────────────── CHART ──────────────────

function ChartCard({
  rows, selectedMetric, onSelectMetric, isLoading,
}: {
  rows: DailyRow[]
  selectedMetric: MetricKey
  onSelectMetric: (k: MetricKey) => void
  isLoading: boolean
}) {
  const sorted = useMemo(
    () => [...rows].sort((a, b) => a.date.localeCompare(b.date)),
    [rows],
  )
  return (
    <div className="ar-card">
      <div className="ar-card-head ar-chart-head">
        <h3>{METRIC_LABELS[selectedMetric]}</h3>
      </div>
      <div className="ar-chart-wrap">
        {isLoading ? (
          <div className="ar-empty">Загружаем…</div>
        ) : sorted.length === 0 ? (
          <div className="ar-empty">Нет данных за период.</div>
        ) : (
          <Chart rows={sorted} metric={selectedMetric} />
        )}
      </div>
      <div className="ar-chart-chips">
        {METRIC_KEYS.map((k) => (
          <button
            key={k}
            className={`ar-chip ${selectedMetric === k ? 'on' : ''}`}
            onClick={() => onSelectMetric(k)}
          >
            {METRIC_LABELS[k]}
          </button>
        ))}
      </div>
    </div>
  )
}

function Chart({ rows, metric }: { rows: DailyRow[]; metric: MetricKey }) {
  const W = 720, H = 280
  const padL = 56, padR = 12, padT = 16, padB = 32
  const values = rows.map((r) => r[metric] as number)
  const max = Math.max(0, ...values)
  const min = Math.min(0, ...values)
  const span = max - min || 1
  const ticks = 5
  const tickStep = span / (ticks - 1)
  const yTicks = Array.from({ length: ticks }, (_, i) => min + tickStep * i)

  const xStep = rows.length > 1 ? (W - padL - padR) / (rows.length - 1) : 0
  const points = rows.map((r, i) => ({
    x: padL + i * xStep,
    y: padT + (1 - ((r[metric] as number) - min) / span) * (H - padT - padB),
    date: r.date,
    value: r[metric] as number,
  }))

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
  const areaPath =
    `M ${points[0].x} ${H - padB} ` +
    points.map((p) => `L ${p.x} ${p.y}`).join(' ') +
    ` L ${points[points.length - 1].x} ${H - padB} Z`

  const kind = METRIC_FORMAT[metric]
  const [hover, setHover] = useState<number | null>(null)

  const stripWidth = points.length > 1 ? xStep : (W - padL - padR)

  return (
    <div className="ar-chart-host">
      <svg className="ar-chart-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id="ar-chart-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--tg-blue, #0080ff)" stopOpacity="0.25" />
            <stop offset="100%" stopColor="var(--tg-blue, #0080ff)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* grid + Y labels */}
        {yTicks.map((v, i) => {
          const y = padT + (1 - (v - min) / span) * (H - padT - padB)
          return (
            <g key={i}>
              <line x1={padL} x2={W - padR} y1={y} y2={y}
                stroke="var(--tg-line-2)" strokeWidth="1" strokeDasharray="3 4" />
              <text x={padL - 8} y={y + 4} textAnchor="end" className="ar-chart-axis">
                {compactNumber(v, kind)}
              </text>
            </g>
          )
        })}

        {/* area + line */}
        <path d={areaPath} fill="url(#ar-chart-fill)" />
        <path d={linePath} fill="none" stroke="var(--tg-blue, #0080ff)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

        {/* X labels */}
        {points.map((p, i) => {
          const step = Math.max(1, Math.ceil(points.length / 6))
          if (i % step !== 0 && i !== points.length - 1) return null
          return (
            <text key={i} x={p.x} y={H - 10} textAnchor="middle" className="ar-chart-axis">
              {shortDate(p.date)}
            </text>
          )
        })}

        {/* hover guideline + emphasized dot */}
        {hover != null && points[hover] && (
          <g>
            <line
              x1={points[hover].x} x2={points[hover].x}
              y1={padT} y2={H - padB}
              stroke="var(--tg-blue, #0080ff)" strokeWidth="1" strokeDasharray="3 3" opacity="0.4"
            />
            <circle cx={points[hover].x} cy={points[hover].y} r="6" fill="white" stroke="var(--tg-blue, #0080ff)" strokeWidth="2.5" />
          </g>
        )}

        {/* dots (always visible) */}
        {points.map((p, i) => (
          <circle key={`d-${i}`} cx={p.x} cy={p.y} r="3" fill="var(--tg-blue, #0080ff)" stroke="white" strokeWidth="1.5" />
        ))}

        {/* invisible hover strips — каждой точке вертикальная полоса */}
        {points.map((p, i) => (
          <rect
            key={`h-${i}`}
            x={p.x - stripWidth / 2}
            y={0}
            width={stripWidth}
            height={H}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover((h) => (h === i ? null : h))}
          />
        ))}
      </svg>

      {hover != null && points[hover] && (() => {
        const pct = points[hover].x / W
        const anchor: 'left' | 'right' | 'center' =
          pct < 0.15 ? 'left' : pct > 0.85 ? 'right' : 'center'
        return (
          <div
            className="ar-chart-tooltip"
            data-anchor={anchor}
            style={{
              left: `${pct * 100}%`,
              top: `${(points[hover].y / H) * 100}%`,
            }}
          >
            <div className="ar-chart-tooltip-date">{formatRu(points[hover].date)}</div>
            <div className="ar-chart-tooltip-value">
              <span className="ar-chart-tooltip-label">{METRIC_LABELS[metric]}</span>
              <b>{fmt(points[hover].value, kind)}</b>
            </div>
          </div>
        )
      })()}
    </div>
  )
}

// ────────────────── METRIC CELLS WITH DATA-BARS ──────────────────

// Метрики с барами: деньги и проценты. Целые числа (количества) — без бара.
function hasBar(kind: 'rub' | 'pct' | 'int' | 'num2'): boolean {
  return kind === 'rub' || kind === 'pct' || kind === 'num2'
}

// Цвет полоски: yellow → orange → red по перцентилю
function barClass(pct: number): 'y' | 'o' | 'r' {
  if (pct >= 0.75) return 'r'
  if (pct >= 0.45) return 'o'
  return 'y'
}

function MetricCell({ value, kind, max }: { value: number; kind: 'rub' | 'pct' | 'int' | 'num2'; max: number }) {
  if (!hasBar(kind) || max <= 0) {
    return <span className="ar-num">{fmt(value, kind)}</span>
  }
  const pct = Math.max(0, Math.min(1, value / max))
  const widthPct = Math.round(pct * 100)
  return (
    <div className="ar-mcell">
      <div className={`ar-mcell-bar ${barClass(pct)}`}>
        <i style={{ width: `${widthPct}%` }} />
      </div>
      <div className="ar-mcell-num">{fmt(value, kind)}</div>
    </div>
  )
}

function compactNumber(v: number, kind: 'rub' | 'pct' | 'int' | 'num2'): string {
  if (kind === 'pct') return v.toFixed(0) + '%'
  const abs = Math.abs(v)
  let s: string
  if (abs >= 1_000_000) s = (v / 1_000_000).toFixed(1) + 'M'
  else if (abs >= 1_000) s = (v / 1_000).toFixed(1) + 'k'
  else s = kind === 'int' ? Math.round(v).toString() : v.toFixed(1)
  return s + (kind === 'rub' ? ' ₽' : '')
}

function shortDate(iso: string): string {
  try {
    const d = new Date(iso + 'T00:00:00')
    return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit' }).format(d)
  } catch { return iso }
}

// ────────────────── DAILY TABLE ──────────────────

function DailyTable({
  rows, totals, visibleColumns, isLoading, isError, errorMessage,
}: {
  rows: DailyRow[]; totals: DailyRow | undefined
  visibleColumns: MetricKey[]
  isLoading: boolean; isError: boolean; errorMessage?: string
}) {
  return (
    <div className="ar-card">
      <div className="ar-card-head">
        <h3>Отчёт по дням</h3>
      </div>
      {isLoading ? (
        <div className="ar-empty">Загружаем…</div>
      ) : isError ? (
        <div className="ar-empty error">{errorMessage || 'Ошибка загрузки'}</div>
      ) : rows.length === 0 ? (
        <div className="ar-empty">За выбранный период данных нет.</div>
      ) : (
        <div className="ar-table-wrap">
          <table className="ar-table">
            <thead>
              <tr>
                <th>Дата</th>
                {visibleColumns.map((k) => <th key={k} className="num">{METRIC_LABELS[k]}</th>)}
              </tr>
            </thead>
            <tbody>
              {[...rows].sort((a, b) => b.date.localeCompare(a.date)).map((r) => (
                <tr key={r.date}>
                  <td>{formatDateDot(r.date)}</td>
                  {visibleColumns.map((k) => {
                    const max = Math.max(0, ...rows.map((x) => x[k] as number))
                    return (
                      <td key={k} className="num">
                        <MetricCell value={r[k] as number} kind={METRIC_FORMAT[k]} max={max} />
                      </td>
                    )
                  })}
                </tr>
              ))}
              {totals && (
                <tr className="ar-total">
                  <td>Итого</td>
                  {visibleColumns.map((k) => (
                    <td key={k} className="num">{fmt(totals[k] as number, METRIC_FORMAT[k])}</td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ────────────────── BY-CAMPAIGN TABLE ──────────────────

type SortKey = MetricKey | 'name'
type SortDir = 'asc' | 'desc'

function ByCampaignTable({
  rows, totals, visibleColumns, isLoading, isError, errorMessage,
}: {
  rows: CampaignRow[]; totals: MetricRow | undefined
  visibleColumns: MetricKey[]
  isLoading: boolean; isError: boolean; errorMessage?: string
}) {
  // Дизайн: все кампании развёрнуты по умолчанию, юзер может коллапсить кликом
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [sortKey, setSortKey] = useState<SortKey>('cost')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const sorted = useMemo(() => {
    const out = [...rows]
    out.sort((a, b) => {
      if (sortKey === 'name') {
        const cmp = a.campaignName.localeCompare(b.campaignName)
        return sortDir === 'asc' ? cmp : -cmp
      }
      const av = a.metrics[sortKey], bv = b.metrics[sortKey]
      return sortDir === 'asc' ? av - bv : bv - av
    })
    return out
  }, [rows, sortKey, sortDir])

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortKey(k); setSortDir('desc') }
  }

  function sortIcon(k: SortKey) {
    if (sortKey !== k) return <span className="ar-sort-icon"> ↕</span>
    return <span className="ar-sort-icon on">{sortDir === 'asc' ? ' ↑' : ' ↓'}</span>
  }

  return (
    <div className="ar-card">
      <div className="ar-card-head">
        <h3>Отчёт по кампаниям</h3>
      </div>
      {isLoading ? (
        <div className="ar-empty">Загружаем…</div>
      ) : isError ? (
        <div className="ar-empty error">{errorMessage || 'Ошибка загрузки'}</div>
      ) : rows.length === 0 ? (
        <div className="ar-empty">Для выбранных кампаний нет данных за период.</div>
      ) : (
        <div className="ar-table-wrap">
          <table className="ar-table">
            <thead>
              <tr>
                <th className="ar-th-sort" onClick={() => toggleSort('name')}>
                  Кампания / объявление{sortIcon('name')}
                </th>
                {visibleColumns.map((k) => (
                  <th key={k} className="num ar-th-sort" onClick={() => toggleSort(k)}>
                    {METRIC_LABELS[k]}{sortIcon(k)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Максимум по каждой метрике — из объединения кампаний и объявлений
                const maxByCol: Record<string, number> = {}
                for (const k of visibleColumns) {
                  let m = 0
                  for (const c of sorted) {
                    m = Math.max(m, c.metrics[k] as number)
                    for (const ad of c.ads) m = Math.max(m, ad.metrics[k] as number)
                  }
                  maxByCol[k] = m
                }
                return sorted.map((c) => {
                  const isOpen = !collapsed[c.campaignID]
                  return (
                    <Fragment key={c.campaignID}>
                      <tr className="ar-row-campaign" onClick={() => setCollapsed((e) => ({ ...e, [c.campaignID]: isOpen }))}>
                        <td>
                          <span className={`ar-chev ${isOpen ? 'on' : ''}`}>▸</span>
                          <code>{c.campaignName}</code>
                          <span className="ar-mute ar-ad-count">{c.ads.length} {pluralAds(c.ads.length)}</span>
                        </td>
                        {visibleColumns.map((k) => (
                          <td key={k} className="num">
                            <MetricCell value={c.metrics[k] as number} kind={METRIC_FORMAT[k]} max={maxByCol[k]} />
                          </td>
                        ))}
                      </tr>
                      {isOpen && c.ads.map((a) => (
                        <tr key={`${c.campaignID}-${a.adID}`} className="ar-row-ad">
                          <td className="ar-ad-title">↳ <span>{a.adTitle}</span></td>
                          {visibleColumns.map((k) => (
                            <td key={k} className="num">
                              <MetricCell value={a.metrics[k] as number} kind={METRIC_FORMAT[k]} max={maxByCol[k]} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </Fragment>
                  )
                })
              })()}
              {totals && (
                <tr className="ar-total">
                  <td>Итого по всем</td>
                  {visibleColumns.map((k) => (
                    <td key={k} className="num">{fmt(totals[k] as number, METRIC_FORMAT[k])}</td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ────────────────── HELPERS ──────────────────

function presetRange(p: Preset): { from: string; to: string } {
  const today = new Date()
  const ymd = (d: Date) => d.toISOString().slice(0, 10)
  switch (p) {
    case 'today': return { from: ymd(today), to: ymd(today) }
    case 'yesterday': {
      const y = new Date(today); y.setDate(y.getDate() - 1)
      return { from: ymd(y), to: ymd(y) }
    }
    case '7d': {
      const f = new Date(today); f.setDate(f.getDate() - 6)
      return { from: ymd(f), to: ymd(today) }
    }
    case '30d': {
      const f = new Date(today); f.setDate(f.getDate() - 29)
      return { from: ymd(f), to: ymd(today) }
    }
    case 'thisMonth': {
      const f = new Date(today.getFullYear(), today.getMonth(), 1)
      return { from: ymd(f), to: ymd(today) }
    }
    case 'lastMonth': {
      const f = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      const t = new Date(today.getFullYear(), today.getMonth(), 0)
      return { from: ymd(f), to: ymd(t) }
    }
    default: return { from: ymd(today), to: ymd(today) }
  }
}

function daysBetween(from: string, to: string): number {
  const f = new Date(from + 'T00:00:00').getTime()
  const t = new Date(to + 'T00:00:00').getTime()
  return Math.floor((t - f) / (24 * 60 * 60 * 1000)) + 1
}

function formatRu(iso: string): string {
  try {
    return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(new Date(iso))
  } catch { return iso }
}

function formatDateDot(iso: string): string {
  if (iso === 'total') return 'Итого'
  return formatRu(iso)
}

function fmt(value: number, kind: 'rub' | 'pct' | 'int' | 'num2'): string {
  if (value == null || isNaN(value)) return '—'
  if (kind === 'int') return value.toLocaleString('ru-RU')
  if (kind === 'pct') return value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%'
  if (kind === 'rub') return value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽'
  return value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatRub(v: number): string { return fmt(v, 'rub') }

function loadVisibleColumns(): MetricKey[] {
  try {
    const raw = localStorage.getItem(VISIBLE_COLUMNS_KEY)
    if (!raw) return DEFAULT_VISIBLE
    const parsed = JSON.parse(raw) as MetricKey[]
    const valid = parsed.filter((k) => METRIC_KEYS.includes(k))
    return valid.length ? valid : DEFAULT_VISIBLE
  } catch { return DEFAULT_VISIBLE }
}

function pluralCampaigns(n: number): string {
  const m10 = n % 10, m100 = n % 100
  if (m10 === 1 && m100 !== 11) return 'кампания'
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'кампании'
  return 'кампаний'
}
function pluralAds(n: number): string {
  const m10 = n % 10, m100 = n % 100
  if (m10 === 1 && m100 !== 11) return 'объявление'
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'объявления'
  return 'объявлений'
}

function Styles() {
  return (
    <style>{`
      .ar-content {
        max-width: 1280px;
        margin: 0 auto;
        padding: 32px var(--tg-content-pad, 32px) 80px;
        width: 100%;
        font-family: var(--tg-font-ui, Inter, sans-serif);
      }
      .hero { margin-bottom: 24px; }
      .hero-row {
        display: flex; justify-content: space-between;
        align-items: flex-end; gap: 16px; flex-wrap: wrap;
      }
      .ar-tabs {
        display: inline-flex;
        background: var(--tg-surface-2);
        padding: 4px;
        border-radius: var(--tg-r-md, 10px);
      }
      .ar-tabs button {
        border: 0; background: transparent;
        padding: 8px 18px; border-radius: 8px;
        font-family: inherit; font-weight: 700; font-size: 13px;
        color: var(--tg-mute); cursor: pointer;
        transition: all var(--tg-dur-fast, 120ms);
      }
      .ar-tabs button.on {
        background: var(--tg-surface, #fff);
        color: var(--tg-ink);
        box-shadow: 0 1px 4px rgba(0,0,0,0.08);
      }
      .hero-eyebrow {
        font-weight: 700; font-size: 11px; letter-spacing: 0.12em;
        text-transform: uppercase; color: var(--tg-mute);
        margin: 0 0 12px;
      }
      .hero-title {
        font-weight: 800; font-size: 38px; line-height: 1.05;
        letter-spacing: -0.028em; color: var(--tg-ink); margin: 0 0 10px;
      }
      .hero-title em {
        font-family: var(--tg-font-italic); font-style: italic; font-weight: 400;
        color: var(--tg-blue); letter-spacing: -0.01em;
      }
      .hero-sub { font-size: 15px; color: var(--tg-mute); margin: 0; max-width: 640px; }

      .ar-filters {
        display: flex; gap: 8px; flex-wrap: wrap;
        margin-bottom: 20px;
        align-items: center;
      }

      .ar-filter-wrap { position: relative; }
      .ar-filter-btn {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 8px 14px;
        background: var(--tg-surface, #fff);
        border: 1px solid var(--tg-line-2);
        border-radius: var(--tg-r-md, 10px);
        font-family: inherit; font-weight: 600; font-size: 13px;
        color: var(--tg-ink-2);
        cursor: pointer;
        transition: all var(--tg-dur-fast, 120ms);
      }
      .ar-filter-btn:hover, .ar-filter-btn.on {
        border-color: var(--tg-blue-soft, rgba(0,128,255,0.4));
        color: var(--tg-blue-press);
      }
      .ar-filter-btn svg { width: 14px; height: 14px; }

      .ar-segments {
        display: inline-flex;
        background: var(--tg-surface-2);
        padding: 3px;
        border-radius: var(--tg-r-md);
      }
      .ar-segments button {
        border: 0; background: transparent;
        padding: 6px 12px; border-radius: 7px;
        font-family: inherit; font-weight: 600; font-size: 12px;
        color: var(--tg-mute); cursor: pointer;
      }
      .ar-segments button.on {
        background: var(--tg-surface, #fff); color: var(--tg-ink);
        box-shadow: 0 1px 3px rgba(0,0,0,0.06);
      }

      .ar-popover {
        position: absolute; top: calc(100% + 6px); left: 0;
        background: var(--tg-surface, #fff);
        border: 1px solid var(--tg-line-2);
        border-radius: var(--tg-r-lg, 14px);
        padding: 14px;
        min-width: 320px; max-width: 480px;
        box-shadow: 0 24px 60px -16px rgba(38,40,45,0.2);
        z-index: 100;
      }
      .ar-popover-wide { min-width: 460px; }
      .ar-popover-head {
        font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
        text-transform: uppercase; color: var(--tg-mute);
        margin-bottom: 10px;
      }
      .ar-popover-presets {
        display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px;
        margin-bottom: 12px;
      }
      .ar-preset {
        padding: 7px 10px; background: var(--tg-surface-2);
        border: 0; border-radius: 8px; font-family: inherit;
        font-weight: 500; font-size: 13px; color: var(--tg-ink-2);
        cursor: pointer; text-align: left;
      }
      .ar-preset:hover { background: var(--tg-blue-bg); color: var(--tg-blue-press); }
      .ar-date-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 10px; }
      .ar-date-row label { display: flex; flex-direction: column; gap: 4px; font-size: 11px; color: var(--tg-mute); font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
      .ar-date-row input {
        padding: 8px 10px; border: 1px solid var(--tg-line-2);
        border-radius: 8px; font-family: inherit; font-size: 13px;
        color: var(--tg-ink);
      }
      .ar-popover-error { color: var(--tg-down); font-size: 12px; margin-bottom: 8px; }
      .ar-popover-foot { display: flex; gap: 8px; justify-content: flex-end; align-items: center; padding-top: 10px; border-top: 1px solid var(--tg-line); }
      .ar-btn-link { background: 0; border: 0; font-family: inherit; font-size: 13px; color: var(--tg-mute); cursor: pointer; }
      .ar-btn-link:hover { color: var(--tg-ink); }
      .ar-btn-primary {
        background: var(--tg-blue); color: white; border: 0;
        padding: 8px 16px; border-radius: 8px;
        font-family: inherit; font-weight: 600; font-size: 13px;
        cursor: pointer;
      }
      .ar-btn-primary:hover { background: var(--tg-blue-hover); }

      .ar-search {
        width: 100%; padding: 9px 12px;
        border: 1px solid var(--tg-line-2); border-radius: 8px;
        font-family: inherit; font-size: 13px; color: var(--tg-ink);
        margin-bottom: 10px;
      }
      .ar-search:focus { outline: 0; border-color: var(--tg-blue); }

      .ar-checkbox-row {
        display: flex; align-items: center; gap: 10px;
        padding: 8px 4px; margin-bottom: 8px;
        cursor: pointer; font-size: 13px; color: var(--tg-ink-2);
        border-bottom: 1px solid var(--tg-line);
      }
      .ar-checkbox-row:hover { color: var(--tg-blue-press); }
      .ar-checkbox-row .ar-mute { margin-left: auto; }
      .ar-checkbox {
        width: 16px; height: 16px; border-radius: 4px;
        border: 1.5px solid var(--tg-line-3); flex-shrink: 0;
        position: relative; transition: all var(--tg-dur-fast, 120ms);
      }
      .ar-checkbox.on {
        background: var(--tg-blue); border-color: var(--tg-blue);
      }
      .ar-checkbox.on::after {
        content: ""; position: absolute;
        left: 4px; top: 1px; width: 5px; height: 9px;
        border: solid white; border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }

      .ar-campaigns-list, .ar-columns-list {
        max-height: 320px; overflow-y: auto;
        display: flex; flex-direction: column; gap: 2px;
      }
      .ar-campaign-opt {
        display: flex; align-items: flex-start; gap: 10px;
        padding: 8px 4px; cursor: pointer; border-radius: 6px;
        font-size: 13px;
      }
      .ar-campaign-opt:hover { background: var(--tg-surface-2); }
      .ar-campaign-meta { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
      .ar-campaign-meta code {
        font-family: var(--tg-font-mono, monospace); font-size: 12px;
        color: var(--tg-ink); word-break: break-all;
      }
      .ar-campaign-ad { font-size: 11.5px; color: var(--tg-mute); line-height: 1.4; }
      .ar-campaign-spend { font-size: 12px; font-variant-numeric: tabular-nums; white-space: nowrap; flex-shrink: 0; }
      .ar-mute { color: var(--tg-mute); }
      .ar-empty {
        padding: 24px; text-align: center; color: var(--tg-mute); font-size: 13px;
      }
      .ar-empty.error { color: var(--tg-down); }

      .ar-card {
        background: var(--tg-surface, #fff);
        border: 1px solid var(--tg-line-2);
        border-radius: var(--tg-r-lg, 14px);
        margin-bottom: 20px;
        overflow: hidden;
      }
      .ar-card-head {
        padding: 18px 24px;
        border-bottom: 1px solid var(--tg-line);
      }
      .ar-card-head h3 { margin: 0; font-size: 16px; font-weight: 700; color: var(--tg-ink); }
      .ar-chart-head h3 { font-size: 20px; }
      .ar-chart-wrap {
        padding: 8px 16px 0;
        min-height: 280px;
        position: relative;
      }
      .ar-chart-svg {
        width: 100%; height: 280px;
        display: block;
      }
      .ar-chart-axis {
        font-size: 10px;
        fill: var(--tg-mute);
        font-family: var(--tg-font-ui, Inter, sans-serif);
      }
      .ar-chart-chips {
        display: flex; flex-wrap: wrap; gap: 6px;
        padding: 14px 18px 18px;
        border-top: 1px solid var(--tg-line);
      }
      .ar-chip {
        padding: 6px 12px;
        background: var(--tg-surface-2);
        border: 1px solid transparent;
        border-radius: var(--tg-r-full, 999px);
        font-family: inherit;
        font-weight: 600; font-size: 12px;
        color: var(--tg-mute);
        cursor: pointer;
        transition: all var(--tg-dur-fast, 120ms);
      }
      .ar-chip:hover {
        background: var(--tg-blue-bg);
        color: var(--tg-blue-press);
      }
      .ar-chip.on {
        background: var(--tg-blue);
        color: white;
        border-color: var(--tg-blue);
      }

      .ar-table-wrap { overflow-x: auto; }
      .ar-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
      }
      .ar-table thead th {
        padding: 10px 14px;
        font-size: 11px; font-weight: 700; letter-spacing: 0.04em;
        text-transform: uppercase; color: var(--tg-mute);
        background: var(--tg-surface-2);
        text-align: left;
        border-bottom: 1px solid var(--tg-line);
        white-space: nowrap;
      }
      .ar-table th.num, .ar-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
      .ar-th-sort { cursor: pointer; user-select: none; }
      .ar-th-sort:hover { color: var(--tg-blue-press); }
      .ar-sort-icon { color: var(--tg-mute-2, #b4c0d0); margin-left: 2px; }
      .ar-sort-icon.on { color: var(--tg-blue); }
      .ar-table tbody td {
        padding: 11px 14px;
        border-bottom: 1px solid var(--tg-line);
        color: var(--tg-ink-2);
        white-space: nowrap;
      }
      .ar-table tbody tr:last-child td { border-bottom: 0; }
      .ar-row-campaign { cursor: pointer; }
      .ar-row-campaign:hover { background: var(--tg-surface-2); }
      .ar-row-campaign td { font-weight: 600; }
      .ar-row-campaign code {
        font-family: var(--tg-font-mono, monospace); font-size: 12.5px;
        color: var(--tg-ink); margin-right: 8px;
      }
      .ar-row-ad td { background: rgba(0, 128, 255, 0.025); color: var(--tg-mute); font-size: 12.5px; }
      .ar-ad-title span { color: var(--tg-ink-2); }
      .ar-ad-count { margin-left: 4px; font-size: 12px; font-weight: 500; }
      .ar-chev {
        display: inline-block; width: 12px;
        color: var(--tg-mute); margin-right: 6px;
        transition: transform 120ms;
      }
      .ar-chev.on { transform: rotate(90deg); }
      .ar-total td {
        background: var(--tg-surface-2);
        font-weight: 800; color: var(--tg-ink);
        border-top: 2px solid var(--tg-line);
      }

      /* metric cells with data-bars */
      .ar-mcell {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 12px;
        align-items: center;
        min-width: 150px;
      }
      .ar-mcell-bar {
        height: 4px;
        background: var(--tg-line);
        border-radius: var(--tg-r-full, 999px);
        position: relative;
        min-width: 60px;
        max-width: 140px;
        overflow: hidden;
      }
      .ar-mcell-bar i {
        display: block;
        height: 100%;
        border-radius: inherit;
        transition: width 200ms ease-out;
      }
      .ar-mcell-bar.y i { background: #F5B400; }
      .ar-mcell-bar.o i { background: #E86920; }
      .ar-mcell-bar.r i { background: var(--tg-down, #ff4000); }
      .ar-mcell-bar.g i { background: var(--tg-up, #00a860); }
      .ar-mcell-num {
        text-align: right;
        font-weight: 600;
        color: var(--tg-ink);
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }
      .ar-num {
        display: inline-block;
        text-align: right;
        font-variant-numeric: tabular-nums;
      }

      /* chart hover */
      .ar-chart-host { position: relative; }
      .ar-chart-tooltip {
        position: absolute;
        background: var(--tg-ink);
        color: white;
        padding: 8px 12px;
        border-radius: 8px;
        font-size: 12px;
        white-space: nowrap;
        pointer-events: none;
        box-shadow: 0 6px 16px -4px rgba(0,0,0,0.3);
        z-index: 10;
      }
      .ar-chart-tooltip[data-anchor="center"] {
        transform: translate(-50%, calc(-100% - 14px));
      }
      .ar-chart-tooltip[data-anchor="left"] {
        transform: translate(-8px, calc(-100% - 14px));
      }
      .ar-chart-tooltip[data-anchor="right"] {
        transform: translate(calc(-100% + 8px), calc(-100% - 14px));
      }
      .ar-chart-tooltip-date {
        font-size: 11px;
        opacity: 0.7;
        margin-bottom: 2px;
      }
      .ar-chart-tooltip-value {
        display: flex; align-items: baseline; gap: 8px;
        font-variant-numeric: tabular-nums;
      }
      .ar-chart-tooltip-label { opacity: 0.7; font-size: 11px; }
      .ar-chart-tooltip b { color: white; font-weight: 700; }
      .ar-chart-svg { cursor: crosshair; }
    `}</style>
  )
}
